import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth';
import { GuestSessionService } from '../services/guest-session';

const TOKEN_KEY = 'auth_token';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const guestSession = inject(GuestSessionService);
  
  // Прямой доступ к localStorage (только на клиенте)
  let token: string | null = null;
  if (typeof window !== 'undefined' && window.localStorage) {
    token = window.localStorage.getItem(TOKEN_KEY);
  }

  const isPublic = isPublicEndpoint(req.url);

  // Для публичных эндпоинтов не добавляем токен (даже если он есть)
  if (isPublic) {
    return next(req);
  }

  // Если есть токен и это защищенный эндпоинт, добавляем заголовок Authorization
  if (token) {
    const clonedReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return next(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Токен живёт 24 часа: гость (клиент без телефона) входит заново тихо и повторяет запрос.
        // Только если сейчас вошёл именно гость — иначе клиент с телефоном молча оказался бы в гостевой записи
        if (error.status === 401 && authService.isGuest() && guestSession.hasCredentials()) {
          return guestSession.reSignIn().pipe(
            switchMap(ok => {
              if (!ok) {
                authService.logout();
                router.navigate(['/login']);
                return throwError(() => error);
              }
              const fresh = authService.getToken();
              return next(fresh ? req.clone({ setHeaders: { Authorization: `Bearer ${fresh}` } }) : req);
            })
          );
        }

        // «Войти как» истёк (токен живёт 30 минут): возвращаем администратора в его сессию, а не разлогиниваем
        if (error.status === 401 && authService.isImpersonating()) {
          if (authService.stopImpersonation()) {
            router.navigate(['/admin/users']);
            return throwError(() => error);
          }
        }

        // При ошибке 401 делаем автоматический logout
        if (error.status === 401) {
          console.warn('Auth interceptor: Got 401 (Unauthorized), performing automatic logout', {
            url: req.url,
            status: error.status
          });

          // Выполняем logout
          authService.logout();

          // Редиректим на страницу логина
          router.navigate(['/login']);
        }

        return throwError(() => error);
      })
    );
  }

  // Если нет токена для защищенного эндпоинта, пропускаем запрос (сервер вернет 401/403)
  return next(req);
};

// Проверка, является ли эндпоинт публичным (не требует авторизации)
function isPublicEndpoint(url: string): boolean {
  const normalizedUrl = url.toLowerCase();

  // Исключения внутри /registration/**, которые ТРЕБУЮТ авторизации (действие
  // от лица уже залогиненного пользователя, а не анонимная регистрация)
  const protectedRegistrationPatterns = [
    '/cargo/registration/upgrade-to-cargo-owner'
  ];
  if (protectedRegistrationPatterns.some(p => normalizedUrl.includes(p))) {
    return false;
  }

  // Публичные эндпоинты (с учетом прокси, который добавляет /cargo префикс)
  const publicPatterns = [
    '/cargo/auth',
    '/cargo/open-api',  // Все эндпоинты open-api публичные
    '/cargo/registration',
    '/assets',
    '/index.html'
  ];

  // Для open-api проверяем более точно - должен начинаться с /cargo/open-api
  if (normalizedUrl.includes('/cargo/open-api')) {
    return true;
  }

  return publicPatterns.some(pattern => {
    const patternLower = pattern.toLowerCase();
    // Для остальных паттернов используем includes
    return normalizedUrl.includes(patternLower);
  });
}
